import { Component } from "react";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";

/**
 * App-level error boundary. On an uncaught render error it logs details to the
 * console and renders a WSTF-styled fallback (black/gold) with Reload + Home
 * actions instead of a blank white screen.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[WSTF] Uncaught UI error:", error, info?.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div data-testid="app-error-boundary"
        className="min-h-screen bg-[#000000] text-slate-200 flex items-center justify-center px-6">
        <div className="relative max-w-xl w-full rounded-2xl border border-[rgba(212,175,55,0.3)] bg-gradient-to-b from-[#141414] to-[#070707] p-8 text-center overflow-hidden">
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(60% 120% at 50% -10%, rgba(212,175,55,0.14) 0%, transparent 60%)" }} />
          <div className="relative">
            <div className="mx-auto mb-5 w-14 h-14 rounded-full border border-[#E5B842]/40 bg-[#E5B842]/10 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-[#E5B842]" />
            </div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#E5B842] font-mono font-bold mb-2">WSTF System Notice</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-50">Something went wrong</h1>
            <p className="mt-3 text-sm text-slate-400">
              An unexpected error interrupted this page. Your data is safe. Reload to continue, or return to the homepage.
            </p>
            <div className="mt-7 flex items-center justify-center gap-3">
              <button data-testid="error-reload-btn" onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#E5B842] text-[#070707] text-sm font-bold uppercase tracking-wide hover:opacity-90"
                style={{ transition: "opacity 0.15s ease" }}>
                <RotateCcw className="w-4 h-4" /> Reload
              </button>
              <a data-testid="error-home-btn" href="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-[#2b2512] text-slate-200 text-sm font-bold uppercase tracking-wide hover:border-[#E5B842]/50"
                style={{ transition: "border-color 0.15s ease" }}>
                <Home className="w-4 h-4" /> Home
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
